import { Flex } from 'foundations-components/components/Flex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import styled from 'foundations-theming/utils';
import { getFileTypeIcon } from '../constants/attachments';
import { DownloadIcon } from 'foundations-assets/icons/components/DownloadIcon';
import { HideIcon } from 'foundations-assets/icons/components/HideIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import I18n from 'I18n';
import { downloadAttachment } from '../utils/downloadAttachment';
import { usageTracker } from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledInteractiveContainer = styled.div.withConfig({
  displayName: "MessageAttachment__StyledInteractiveContainer"
})(({
  theme,
  $isInteractive,
  $inlineSize
}) => Object.assign({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.space['400'],
  inlineSize: $inlineSize,
  minInlineSize: '240px',
  blockSize: '58px',
  backgroundColor: theme.color['fill-surface-default'],
  padding: theme.space['200'],
  borderRadius: theme.borderRadius['400'],
  borderWidth: theme.borderWidth['100'],
  borderStyle: 'solid',
  borderColor: theme.color['border-core-subtle'],
  cursor: $isInteractive ? 'pointer' : 'default'
}, $isInteractive && {
  '&:hover, &[data-vr-state="hover"]': {
    backgroundColor: theme.color['fill-surface-default-hover']
  },
  '&:focus-visible': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
export const MessageAttachment = ({
  attachment,
  sensitiveAttachment,
  customSubtitle,
  inThread
}) => {
  const title = attachment ? attachment.fileMetadata.name : I18n.text('customer-portal-app.ticketThread.messageContent.attachment.errorTitle');
  const subtitle = sensitiveAttachment ? I18n.text('customer-portal-app.ticketThread.messageContent.attachment.sensitiveDescription') : attachment ? attachment.fileMetadata.type : I18n.text('customer-portal-app.ticketThread.messageContent.attachment.errorDescription');
  const FileIcon = attachment ? getFileTypeIcon({
    mimeType: attachment.fileMetadata.type,
    ext: attachment.fileMetadata.extension
  }) : WarningIcon;
  const handleDownload = () => {
    if (attachment) {
      usageTracker.track('Customer Portal App', 'Threads Discovered', {
        action: 'attachment-downloaded',
        location: inThread ? 'thread' : 'panel'
      });
      void downloadAttachment(attachment.fileMetadata);
    }
  };
  const isInteractive = Boolean(attachment) && !sensitiveAttachment;
  const PreviewIcon = sensitiveAttachment ? HideIcon : FileIcon;
  return /*#__PURE__*/_jsx(Tooltip, {
    title: isInteractive ? I18n.text('customer-portal-app.ticketThread.messageContent.attachment.download') : undefined,
    children: /*#__PURE__*/_jsxs(StyledInteractiveContainer, {
      $isInteractive: isInteractive,
      $inlineSize: inThread ? '240px' : '100%',
      onClick: isInteractive ? handleDownload : undefined,
      role: isInteractive ? 'button' : undefined,
      tabIndex: isInteractive ? 0 : undefined,
      "aria-label": isInteractive ? I18n.text('customer-portal-app.ticketThread.messageContent.attachment.download') : undefined,
      onKeyDown: event => {
        if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          handleDownload();
        }
      },
      children: [/*#__PURE__*/_jsx(Flex, {
        inlineSize: "40px",
        blockSize: "40px",
        borderRadius: "300",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "fill-surface-recessed",
        children: /*#__PURE__*/_jsx(PreviewIcon, {
          size: "300",
          "aria-hidden": "true"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "100",
        flex: "1",
        minInlineSize: "0",
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: title,
          children: /*#__PURE__*/_jsx(View, {
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              color: "text-core-default",
              truncate: true,
              children: title
            })
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: 'detail-100',
          color: 'text-core-default',
          truncate: true,
          children: customSubtitle || subtitle
        })]
      }), attachment && !sensitiveAttachment && /*#__PURE__*/_jsx(DownloadIcon, {
        size: "300",
        color: "icon-core-subtle",
        "aria-hidden": "true"
      })]
    })
  });
};